import { BASE_URL } from 'config';
import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { Button, Avatar, CircularProgress, Typography, Box } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import axios from "axios";
import GiftSection from "./GiftSection";

const BundleOfferSection = forwardRef((props, ref) => {
  const [bundleName, setBundleName] = useState("");
  const [image, setImage] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [minProducts, setMinProducts] = useState(1);
  const [maxProducts, setMaxProducts] = useState(1);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productDetails, setProductDetails] = useState([]);
  const giftSectionRef = useRef();
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchCategories = async () => {
      setCategoriesLoading(true);
      try {
        const token = localStorage.getItem("user-token");
        const response = await axios.get(
          BASE_URL + "/manager/categories",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.data.status && Array.isArray(response.data.data)) {
          // Flatten categories and subcategories
          const flat = [];
          response.data.data.forEach((cat) => {
            flat.push({ id: cat.id, name: cat.name });
            if (Array.isArray(cat.children)) {
              cat.children.forEach((child) => {
                flat.push({ id: child.id, name: `${cat.name} > ${child.name}` });
              });
            }
          });
          setCategories(flat);
        }
      } catch (e) {
        setCategories([]);
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      setProductsLoading(true);
      try {
        const token = localStorage.getItem("user-token");
        const response = await axios.get(BASE_URL + "/manager/products", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.data.status && Array.isArray(response.data.data)) {
          setProducts(response.data.data);
        } else {
          setProducts([]);
        }
      } catch (e) {
        setProducts([]);
      } finally {
        setProductsLoading(false);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    setProductDetails(
      selectedProducts.map((product) => {
        const existing = productDetails.find((p) => p.id === product.id);
        return (
          existing || {
            id: product.id,
            name: product.name,
            image: product.medias && product.medias.length > 0 ? product.medias[0].file_name : "",
            minQty: 1,
            maxQty: 1,
            defaultQty: 1,
            required: false,
          }
        );
      })
    );
    // eslint-disable-next-line
  }, [selectedProducts]);

  const handleProductDetailChange = (id, field, value) => {
    setProductDetails((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, [field]: field === "required" ? value : Number(value) } : p
      )
    );
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setImageUrl(URL.createObjectURL(file));
    }
  };

  const handleSave = () => {
    const newErrors = {};
    if (!bundleName) newErrors.bundleName = "اسم الحزمة إجباري";
    if (selectedProducts.length === 0)
      newErrors.selectedProducts = "يجب اختيار منتج واحد على الأقل";
    if (minProducts < 1) newErrors.minProducts = "الحد الأدنى يجب أن يكون 1 أو أكثر";
    if (maxProducts < 1) newErrors.maxProducts = "الحد الأقصى يجب أن يكون 1 أو أكثر";
    if (maxProducts < minProducts)
      newErrors.maxProducts = "الحد الأقصى لا يمكن أن يكون أقل من الحد الأدنى";
    for (const p of productDetails) {
      if (p.minQty < 1) newErrors[`minQty_${p.id}`] = "الحد الأدنى يجب أن يكون 1 أو أكثر";
      if (p.maxQty < 1) newErrors[`maxQty_${p.id}`] = "الحد الأقصى يجب أن يكون 1 أو أكثر";
      if (p.defaultQty < 1)
        newErrors[`defaultQty_${p.id}`] = "الكمية الافتراضية يجب أن تكون 1 أو أكثر";
      if (p.maxQty < p.minQty)
        newErrors[`maxQty_${p.id}`] = "الحد الأقصى لا يمكن أن يكون أقل من الحد الأدنى";
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    const giftData = giftSectionRef.current ? giftSectionRef.current.getGiftData() : {};
    const data = {
      name: bundleName,
      description: "عرض جديد",
      promotion_type_id: 1,
      start_date: "",
      end_date: "",
      is_super_offer: 0,
      target_audience: "all",
      is_active: "1",
      rewards: giftData.rewards,
      bundle: {
        name: bundleName,
        category_id: selectedCategory.id,
        min_selections: minProducts,
        max_selections: maxProducts,
        bundle_products: productDetails.map((p) => ({
          product_id: p.id,
          is_required: p.required ? 1 : 0,
          default_quantity: p.defaultQty,
          min_quantity: p.minQty,
          max_quantity: p.maxQty,
        })),
      },
    };
    console.log("بيانات العرض:", data);
  };

  useImperativeHandle(ref, () => ({
    getBundleData: () => {
      return {
        bundle: {
          name: bundleName,
          category_id: selectedCategory?.id,
          min_selections: minProducts,
          max_selections: maxProducts,
          bundle_products: productDetails.map((p) => ({
            product_id: p.id,
            is_required: p.required ? 1 : 0,
            default_quantity: p.defaultQty,
            min_quantity: p.minQty,
            max_quantity: p.maxQty,
          })),
        },
      };
    },
    getGiftData: () => {
      return giftSectionRef.current ? giftSectionRef.current.getGiftData() : {};
    },
    reset: () => {
      setBundleName("");
      setImage(null);
      setImageUrl("");
      setSelectedCategory(null);
      setMinProducts(1);
      setMaxProducts(1);
      setSelectedProducts([]);
      setProductDetails([]);
      setErrors({});
      if (giftSectionRef.current) {
        giftSectionRef.current.reset();
      }
    },
  }));

  return (
    <MDBox
      display="flex"
      flexDirection="column"
      gap={3}
      p={{ xs: 1, sm: 2, md: 3 }}
      boxShadow={3}
      borderRadius={4}
      bgcolor="#fff"
      mb={4}
      sx={{
        width: { xs: "100%", md: "90%" },
        margin: "20px auto",
        backgroundColor: "#fff !important",
        overflowX: "auto",
      }}
    >
      <MDTypography variant="h6" fontWeight="bold" mb={1}>
        بيانات عرض الحزمة
      </MDTypography>
      <MDBox display="flex" gap={2} alignItems="center" flexDirection={{ xs: "column", md: "row" }}>
        <MDInput
          label="اسم الحزمة"
          value={bundleName}
          onChange={(e) => setBundleName(e.target.value)}
          sx={{ flex: 2, width: { xs: "100%", md: "auto" } }}
          fullWidth
          inputProps={{ dir: "rtl" }}
        />
        <MDInput
          label="أقل عدد منتجات"
          type="number"
          value={minProducts}
          onChange={(e) => setMinProducts(e.target.value)}
          sx={{ flex: 1, width: { xs: "100%", md: "auto" } }}
          fullWidth
          inputProps={{ min: 1, dir: "rtl" }}
        />
        <MDInput
          label="أعلى عدد منتجات"
          type="number"
          value={maxProducts}
          onChange={(e) => setMaxProducts(e.target.value)}
          sx={{ flex: 1, width: { xs: "100%", md: "auto" } }}
          fullWidth
          inputProps={{ min: 1, dir: "rtl" }}
        />
        <Autocomplete
          options={categories}
          loading={categoriesLoading}
          getOptionLabel={(option) => option.name || ""}
          value={selectedCategory}
          onChange={(_, newValue) => setSelectedCategory(newValue)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <MDInput
              {...params}
              label="اختر الفئة"
              sx={{ flex: 2, width: { xs: "100%", md: "auto" } }}
              fullWidth
              inputProps={{ ...params.inputProps, dir: "rtl" }}
            />
          )}
          sx={{ flex: 2, height: 45, width: { xs: "100%", md: "auto" } }}
        />
      </MDBox>
      {props.bundleName && (
        <Typography color="error" fontSize={13}>
          {props.bundleName}
        </Typography>
      )}
      {props.category && (
        <Typography color="error" fontSize={13}>
          {props.category}
        </Typography>
      )}
      {(errors.minProducts || errors.maxProducts) && (
        <Typography color="error" fontSize={13}>
          {errors.minProducts || errors.maxProducts}
        </Typography>
      )}
      <Autocomplete
        multiple
        options={products}
        loading={productsLoading}
        getOptionLabel={(option) => option.name || ""}
        value={selectedProducts}
        onChange={(_, newValue) => setSelectedProducts(newValue)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            key={option.id}
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <Avatar
              src={option.medias && option.medias.length > 0 ? option.medias[0].file_name : ""}
              alt={option.name}
              sx={{ width: 32, height: 32 }}
            />
            <Typography variant="body2">{option.name}</Typography>
          </Box>
        )}
        renderInput={(params) => (
          <MDInput
            {...params}
            label="اختر منتجات الحزمة"
            fullWidth
            inputProps={{ ...params.inputProps, dir: "rtl" }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {productsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        sx={{ minWidth: { xs: "100%", md: 400 } }}
      />
      {props.bundleProducts && (
        <Typography color="error" fontSize={13}>
          {props.bundleProducts}
        </Typography>
      )}
      {errors.selectedProducts && (
        <Typography color="error" fontSize={13}>
          {errors.selectedProducts}
        </Typography>
      )}
      {productDetails.length > 0 && (
        <MDBox mt={2} sx={{ overflowX: "auto" }}>
          <MDTypography variant="subtitle1" fontWeight="bold" mb={1}>
            المنتجات المختارة:
          </MDTypography>
          <Box
            component="table"
            sx={{
              width: "100%",
              borderCollapse: "collapse",
              background: "#fafafa",
              borderRadius: 2,
            }}
          >
            <Box component="thead">
              <Box component="tr" sx={{ background: "#f0f0f0" }}>
                <Box component="th" sx={{ p: 1, border: "1px solid #eee" }}>
                  المنتج
                </Box>
                <Box component="th" sx={{ p: 1, border: "1px solid #eee" }}>
                  أقل كمية
                </Box>
                <Box component="th" sx={{ p: 1, border: "1px solid #eee" }}>
                  أعلى كمية
                </Box>
                <Box component="th" sx={{ p: 1, border: "1px solid #eee" }}>
                  الكمية الافتراضية
                </Box>
                <Box component="th" sx={{ p: 1, border: "1px solid #eee" }}>
                  مطلوب؟
                </Box>
              </Box>
            </Box>
            <Box component="tbody">
              {productDetails.map((p) => (
                <Box component="tr" key={p.id}>
                  <Box
                    component="td"
                    sx={{
                      p: 1,
                      border: "1px solid #eee",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Avatar src={p.image} alt={p.name} sx={{ width: 32, height: 32, ml: 1 }} />
                    <Typography variant="body2">{p.name}</Typography>
                  </Box>
                  <Box component="td" sx={{ p: 1, border: "1px solid #eee" }}>
                    <MDInput
                      type="number"
                      value={p.minQty}
                      onChange={(e) => handleProductDetailChange(p.id, "minQty", e.target.value)}
                      inputProps={{ min: 1, dir: "rtl" }}
                      sx={{ width: 80 }}
                    />
                    {errors[`minQty_${p.id}`] && (
                      <Typography color="error" fontSize={12}>
                        {errors[`minQty_${p.id}`]}
                      </Typography>
                    )}
                  </Box>
                  <Box component="td" sx={{ p: 1, border: "1px solid #eee" }}>
                    <MDInput
                      type="number"
                      value={p.maxQty}
                      onChange={(e) => handleProductDetailChange(p.id, "maxQty", e.target.value)}
                      inputProps={{ min: 1, dir: "rtl" }}
                      sx={{ width: 80 }}
                    />
                    {errors[`maxQty_${p.id}`] && (
                      <Typography color="error" fontSize={12}>
                        {errors[`maxQty_${p.id}`]}
                      </Typography>
                    )}
                  </Box>
                  <Box component="td" sx={{ p: 1, border: "1px solid #eee" }}>
                    <MDInput
                      type="number"
                      value={p.defaultQty}
                      onChange={(e) =>
                        handleProductDetailChange(p.id, "defaultQty", e.target.value)
                      }
                      inputProps={{ min: 1, dir: "rtl" }}
                      sx={{ width: 80 }}
                    />
                    {errors[`defaultQty_${p.id}`] && (
                      <Typography color="error" fontSize={12}>
                        {errors[`defaultQty_${p.id}`]}
                      </Typography>
                    )}
                  </Box>
                  <Box component="td" sx={{ p: 1, border: "1px solid #eee", textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={p.required}
                      onChange={(e) =>
                        handleProductDetailChange(p.id, "required", e.target.checked)
                      }
                      style={{ width: 18, height: 18 }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </MDBox>
      )}
      <MDBox display="flex" alignItems="center" gap={2} flexDirection={{ xs: "column", sm: "row" }}>
        <Button
          variant="outlined"
          component="label"
          sx={{
            fontWeight: "bold",
            backgroundColor: "#fff",
            color: "#000",
            width: { xs: "100%", sm: "auto" },
          }}
        >
          تحميل صورة الحزمة
          <input type="file" accept="image/*" hidden onChange={handleImageChange} />
        </Button>
        {imageUrl && (
          <Avatar
            src={imageUrl}
            alt="صورة الحزمة"
            sx={{ width: 56, height: 56, mx: { xs: "auto", sm: 0 } }}
          />
        )}
      </MDBox>
      <GiftSection ref={giftSectionRef} error={props.rewardsError} giftTypes={props.giftTypes} />
    </MDBox>
  );
});

export default BundleOfferSection;
